import React, { useEffect, useRef, useState } from 'react';

import { AttrsHelper } from '../../../sb-helpers/attrs-helper';

export type RevealProps = {
  /**
   * Whether it arrives and leaves under its own animation. Off by default: something that appears
   * because there is now a reason for it is not news, and the room it takes is the caller's to
   * reserve either way.
   *
   * On, it is still unmounted when there is no reason for it - it just outlives `isActive` long
   * enough to see itself out.
   */
  animate?: boolean;
  children?: React.ReactNode;
  /**
   * Whether there is a reason for it. **It renders nothing when there is not**, rather than hiding
   * itself, so nothing has to keep a stylesheet in step with it - and whatever room it takes when
   * it appears is the caller's to reserve.
   */
  isActive: boolean;
  name?: string;
};

/**
 * Something that comes and goes. It draws nothing of its own and says nothing about what is inside
 * it: what appears, and where it sits, are the caller's.
 */
export const Reveal = ({
  animate = false,
  children,
  isActive,
  name,
}: RevealProps) => {
  const [isLeaving, setIsLeaving] = useState(false);
  const wasActiveRef = useRef(isActive);

  /*
    **The only reason this outlives `isActive`.** Unmounting on the spot leaves nothing for a
    leaving animation to run on, so the node is held back for exactly as long as that animation
    takes - and it is the animation ending, not a timer here, that says when it is over.
  */
  useEffect(() => {
    /*
      `!animate` lets go as well, because the class that carries the animation goes with it: held
      back without one, nothing would ever fire the `animationend` that unmounts this, and the node
      stayed on the page - still clickable, since even its `pointer-events` rule is scoped to the
      animated class.
    */
    if (isActive || !animate) setIsLeaving(false);
    else if (wasActiveRef.current) setIsLeaving(true);

    wasActiveRef.current = isActive;
  }, [animate, isActive]);

  if (!isActive && !isLeaving) return null;

  return (
    <div
      className={AttrsHelper.formatClassname(
        'reveal',
        animate && 'reveal-animated',
        isLeaving && 'reveal-leaving'
      )}
      data-test={name}
      /*
        Only this element's own animations, because `animationend` bubbles and anything inside that
        eases would otherwise report the exit as finished before it had started.
      */
      onAnimationEnd={event => {
        if (event.target === event.currentTarget) setIsLeaving(false);
      }}
    >
      {children}
    </div>
  );
};
